// Обратная связь из шапки: общее для окна замечания (src/ui/Feedback.tsx),
// страницы взрослого и админ-страницы (src/screens/Admin.tsx).

import type { Id } from "../../convex/_generated/dataModel";

/** Кто пишет. Ключ ученика или взрослого — сервер сам решит, чья нить. */
export type FeedbackAs = { learnerKey: string } | { tutorKey: string };

/**
 * Где открыли окно. Ставится само, человек его не заполняет: «что вы
 * делали» — первый вопрос к любому сообщению о сбое, и ответ на него у
 * приложения уже есть.
 */
export type FeedbackPlace = {
  /** Экран кодом: `solve`, `theory`, `tutor`… */
  screen: string;
  /** Экран словами, как в шапке: «Задача 3 из 7». */
  where: string;
  itemKey?: string;
  skillId?: string;
};

/**
 * Открыть окно обратной связи не из шапки, а с места: «другое решение» и
 * «написать, что именно» под разбором. Окно одно — то, что в шапке; сюда
 * приходит только другое место и подсказка в пустом поле.
 */
export type FeedbackRequest = { place: FeedbackPlace; hint: string };

const listeners = new Set<(r: FeedbackRequest) => void>();

export function requestFeedback(r: FeedbackRequest): void {
  listeners.forEach((fn) => fn(r));
}

/** Подписка окна на запросы; возвращает отписку. */
export function onFeedbackRequest(fn: (r: FeedbackRequest) => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export type NoteStatus = "new" | "accepted" | "question" | "taken" | "done" | "declined";

/** Подписи статусов — одни на окне автора, у взрослого и в админке. */
export const STATUS_LABEL: Record<NoteStatus, string> = {
  new: "новое",
  question: "нужно уточнение",
  accepted: "принято",
  taken: "в разработке",
  done: "реализовано",
  declined: "отклонено",
};

/** Порядок в сводке и в списке выбора: сначала то, что ждёт решения. */
export const STATUS_ORDER: NoteStatus[] = [
  "new",
  "question",
  "accepted",
  "taken",
  "done",
  "declined",
];

/** Сколько скриншотов берёт одно сообщение — столько же, сколько сервер. */
export const MAX_SHOTS = 5;

/**
 * Устройство одной строкой: размер окна, браузер, система. Без полного
 * user-agent — из него разработчику нужны три слова, а в карточке он занимает
 * три строки.
 */
export function describeDevice(): string {
  try {
    const ua = navigator.userAgent;
    const browser = /Edg\//.test(ua)
      ? "Edge"
      : /YaBrowser\//.test(ua)
        ? "Яндекс"
        : /OPR\//.test(ua)
          ? "Opera"
          : /Firefox\//.test(ua)
            ? "Firefox"
            : /Chrome\//.test(ua)
              ? "Chrome"
              : /Safari\//.test(ua)
                ? "Safari"
                : "браузер";
    const os = /iPhone|iPad|iPod/.test(ua)
      ? "iOS"
      : /Android/.test(ua)
        ? "Android"
        : /Mac OS X/.test(ua)
          ? navigator.maxTouchPoints > 1
            ? "iPadOS"
            : "macOS"
          : /Windows/.test(ua)
            ? "Windows"
            : /Linux/.test(ua)
              ? "Linux"
              : "";
    const theme = document.documentElement.dataset.theme === "dark" ? " · тёмная тема" : "";
    return [`${window.innerWidth}×${window.innerHeight}`, browser, os]
      .filter((s) => s !== "")
      .join(" · ") + theme;
  } catch {
    return "";
  }
}

/** Длинная сторона скриншота после сжатия. Текст интерфейса на ней читается. */
const MAX_SIDE = 1600;

/**
 * Сжать снимок до загрузки. Скриншот с ретина-экрана — 3–6 МБ PNG, и хранить
 * его месяц ради того, чтобы прочитать надпись на кнопке, незачем. WebP там,
 * где браузер его пишет; где нет (старый Safari), `toBlob` сам отдаёт PNG —
 * тип берётся из результата, а не из запроса.
 */
export async function shrinkImage(file: Blob): Promise<Blob> {
  if (!file.type.startsWith("image/")) throw new Error("это не картинка");
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    // HEIC с iPhone и прочее, чего браузер не рисует: отправляем как есть.
    return file;
  }
  const scale = Math.min(1, MAX_SIDE / Math.max(bitmap.width, bitmap.height));
  if (scale === 1 && file.size < 400_000) {
    bitmap.close();
    return file;
  }
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(bitmap.width * scale);
  canvas.height = Math.round(bitmap.height * scale);
  const g = canvas.getContext("2d");
  if (g === null) {
    bitmap.close();
    return file;
  }
  g.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  bitmap.close();
  const out = await new Promise<Blob | null>((ok) => canvas.toBlob(ok, "image/webp", 0.85));
  return out !== null && out.size < file.size ? out : file;
}

/** Загрузить снимки по одному: адрес загрузки одноразовый. */
export async function uploadShots(
  shots: Blob[],
  getUrl: () => Promise<string>,
): Promise<Id<"_storage">[]> {
  const ids: Id<"_storage">[] = [];
  for (const shot of shots) {
    const url = await getUrl();
    const res = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": shot.type || "application/octet-stream" },
      body: shot,
    });
    if (!res.ok) throw new Error(`скриншот не загрузился: ${res.status}`);
    const body = (await res.json()) as { storageId: string };
    ids.push(body.storageId as Id<"_storage">);
  }
  return ids;
}

/** Дата и время коротко: «25.09, 16:40». Год — только если не этот. */
export function when(at: number): string {
  const d = new Date(at);
  const sameYear = d.getFullYear() === new Date().getFullYear();
  return d.toLocaleString("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    ...(sameYear ? {} : { year: "numeric" }),
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** Дата без времени: «25.10». */
export function day(at: number): string {
  return new Date(at).toLocaleDateString("ru-RU", { day: "2-digit", month: "2-digit" });
}
